import React, { useState } from 'react';
import { hoverable } from '../../themes/utils/responsiveHover';
import { Divider, Menu, MenuItem, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ConfirmationDialog from '../common/ConfirmationDialog';

/**
 * SidebarItemMenu — Reusable context menu for sidebar items (agents, plans).
 * Renders the MUI Menu + ConfirmationDialog for delete.
 * The parent manages anchorEl state and passes it here.
 *
 * Props:
 * - anchorEl: HTMLElement | null — DOM element to anchor the menu to (null = closed)
 * - selectedItem: the item object being acted on
 * - onClose: () => void — close the menu
 * - onEdit: (item) => void — edit action
 * - onDelete: (item) => void — delete action (called after confirmation)
 * - onCollections: (item) => void — collections action (optional, only for agents)
 * - onMemory: (item) => void — structured memory action (optional, only for agents)
 * - onNewChat: (item) => void — start a new chat with the item (optional, agents only)
 * - onToggleHidden: (item) => void — toggle sidebar visibility (optional, agents only)
 * - isHidden: boolean — current hidden state of the selected item (for label/icon)
 * - deleteConfirmTitle/Message/Text: i18n strings for the confirmation dialog
 * - editLabel/deleteLabel/collectionsLabel/newChatLabel/hideLabel/showLabel: i18n strings
 */
const SidebarItemMenu = ({
  anchorEl,
  selectedItem,
  onClose,
  onEdit,
  onDelete,
  onCollections,
  onMemory,
  onNewChat,
  onToggleHidden,
  isHidden = false,
  deleteConfirmTitle,
  deleteConfirmMessage,
  deleteConfirmText,
  editLabel = 'Editar',
  deleteLabel = 'Eliminar',
  collectionsLabel = 'Colecciones',
  memoryLabel = 'Memoria',
  newChatLabel = 'Iniciar un chat',
  hideLabel = 'Ocultar del sidebar',
  showLabel = 'Mostrar en sidebar',
  skipConfirmation = false,
  hideDelete = false,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleEdit = () => {
    const item = selectedItem;
    onClose();
    if (item) onEdit(item);
  };

  const handleCollections = () => {
    const item = selectedItem;
    onClose();
    if (item) onCollections(item);
  };

  const handleMemory = () => {
    const item = selectedItem;
    onClose();
    if (item) onMemory(item);
  };

  const handleNewChat = () => {
    const item = selectedItem;
    onClose();
    if (item) onNewChat(item);
  };

  const handleToggleHidden = () => {
    const item = selectedItem;
    onClose();
    if (item) onToggleHidden(item);
  };

  const handleDeleteClick = () => {
    if (skipConfirmation) {
      const item = selectedItem;
      onClose();
      if (item) onDelete(item);
      return;
    }
    setItemToDelete(selectedItem);
    onClose();
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    if (itemToDelete) onDelete(itemToDelete);
    setItemToDelete(null);
  };

  return (
    <>
      <Menu
        open={!!anchorEl}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {onNewChat && (
          <MenuItem
            onClick={handleNewChat}
            sx={{
              gap: 1.5,
              minWidth: 180,
              ...hoverable({ backgroundColor: 'action.hover' }),
            }}
          >
            <AddCommentOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography>{newChatLabel}</Typography>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleEdit}
          sx={{
            gap: 1.5,
            minWidth: 180,
            ...hoverable({ backgroundColor: 'action.hover' }),
          }}
        >
          <EditIcon fontSize="small" color="primary" />
          <Typography>{editLabel}</Typography>
        </MenuItem>
        {onCollections && (
          <MenuItem
            onClick={handleCollections}
            sx={{
              gap: 1.5,
              minWidth: 180,
              ...hoverable({ backgroundColor: 'action.hover' }),
            }}
          >
            <CollectionsBookmarkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography>{collectionsLabel}</Typography>
          </MenuItem>
        )}
        {onMemory && (
          <MenuItem
            onClick={handleMemory}
            sx={{
              gap: 1.5,
              minWidth: 180,
              ...hoverable({ backgroundColor: 'action.hover' }),
            }}
          >
            <PsychologyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography>{memoryLabel}</Typography>
          </MenuItem>
        )}
        {onToggleHidden && <Divider />}
        {onToggleHidden && (
          <MenuItem
            onClick={handleToggleHidden}
            sx={{
              gap: 1.5,
              minWidth: 180,
              ...hoverable({ backgroundColor: 'action.hover' }),
            }}
          >
            {isHidden
              ? <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              : <VisibilityOffIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            <Typography>{isHidden ? showLabel : hideLabel}</Typography>
          </MenuItem>
        )}
        {!hideDelete && <MenuItem
          onClick={handleDeleteClick}
          sx={{
            gap: 1.5,
            minWidth: 180,
            '@media (hover: hover) and (pointer: fine)': {
              '&:hover': skipConfirmation
                ? { backgroundColor: 'action.hover' }
                : {
                    backgroundColor: 'error.light',
                    '& .MuiTypography-root': { color: 'error.contrastText' },
                    '& .MuiSvgIcon-root': { color: 'error.contrastText' },
                  },
            },
          }}
        >
          {skipConfirmation
            ? <ArchiveOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            : <DeleteIcon fontSize="small" color="error" />
          }
          <Typography color={skipConfirmation ? 'text.primary' : 'error.main'}>{deleteLabel}</Typography>
        </MenuItem>}
      </Menu>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={deleteConfirmTitle}
        message={deleteConfirmMessage}
        confirmText={deleteConfirmText}
      />
    </>
  );
};

export default SidebarItemMenu;
